import { useEffect, useState } from 'react';

/**
 * The legal links, in the footer of every surface.
 *
 * THE FALLBACK LIST IS NOT DECORATION. The component fetches the live
 * index from /api/policies, but renders a committed list first so the
 * links are present before that request lands and still present if it
 * fails. A footer whose links appear a second late, or not at all on a
 * flaky connection, is a footer people do not find.
 *
 * THE FALLBACK AND THE REGISTRY ARE ASSERTED TO AGREE by
 * tests/db/trust-safety.test.ts, which reads this array out of this file
 * and compares it with POLICIES. Without that check the two drift, and
 * the failure is silent: the footer keeps linking to a document that has
 * been renamed, and the link 404s for exactly the people who went
 * looking for it.
 */
export const FOOTER_POLICIES = [
  { slug: 'terms-of-service', title: 'Terms of Service' },
  { slug: 'privacy-policy', title: 'Privacy Policy' },
  { slug: 'acceptable-use', title: 'Acceptable Use' },
  { slug: 'seller-agreement', title: 'Seller Agreement' },
  { slug: 'copyright-takedown', title: 'Copyright and Takedown' },
  { slug: 'refund-cancellation', title: 'Refunds and Cancellation' },
  { slug: 'sms-messaging', title: 'SMS Messaging' },
] as const;

interface PolicyLink { slug: string; title: string }

/** The index, read once per page load and shared by every footer on it. */
let policyIndexPromise: Promise<PolicyLink[] | null> | null = null;
function loadPolicyIndex(): Promise<PolicyLink[] | null> {
  policyIndexPromise ??= fetch('/api/policies')
    .then((r) => (r.ok ? r.json() : null))
    .then((body: { items?: PolicyLink[] } | null) => (body?.items?.length ? body.items.map((p) => ({ slug: p.slug, title: p.title })) : null))
    .catch(() => { policyIndexPromise = null; return null; });
  return policyIndexPromise;
}

/**
 * Rendered as a <div>, not a <footer>: it sits INSIDE whichever footer a
 * page already has (`PageFooter` on the public screens, the home page's
 * own), and two footers on one page is a landmark nobody asked for.
 * `label` is the nav's accessible name, translated by the caller.
 */
export function LegalFooter({ compact = false, collapse = false, label = 'Policies' }: { compact?: boolean; collapse?: boolean; label?: string }) {
  const [items, setItems] = useState<PolicyLink[]>([...FOOTER_POLICIES]);

  useEffect(() => {
    let live = true;
    void loadPolicyIndex().then((list) => { if (live && list) setItems(list); });
    return () => { live = false; };
  }, []);

  if (compact && collapse) {
    return (
      <div className="legal-footer legal-footer--compact">
        <details className="legal-disclosure">
          <summary>Legal & policies</summary>
          <nav aria-label={label}>
            <ul>
              {items.map((p) => (
                <li key={p.slug}>
                  <a href={`/legal/${p.slug}`}>{p.title}</a>
                </li>
              ))}
            </ul>
          </nav>
        </details>
      </div>
    );
  }

  return (
    <div className="legal-footer">
      <nav aria-label={label}>
        <ul>
          {items.map((p) => (
            <li key={p.slug}>
              <a href={`/legal/${p.slug}`}>{p.title}</a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
